import { SmsCreditsBalance } from '$features/settings/components/sms/sms-credits-balance'
import { PurchaseCredits } from '$features/settings/components/sms/purchase-credits'
import { SmsCreditsTransactionHistory } from '$features/settings/components/sms/sms-credits-transaction-history'
import { SmsRemindersSettings } from '$features/settings/components/sms/sms-reminders-settings'
import { TabLayout } from '$features/settings/layouts/tab'
import { SettingsRoutePath } from '$features/settings/routes/protected'
import { Breadcrumb, Divider, Text, ToastHelper } from '$components'
import { SettingsIcon } from '$components'
import { usePageTitle } from '$components'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { smsCreditsIndexOptions, smsCreditsIndexQueryKey, smsCreditsTransactionsOptions, smsCreditsTransactionsQueryKey } from '$api-client/@tanstack/react-query.gen'
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

const TRANSACTIONS_PER_PAGE = 10

export function NotificationsTab() {
  usePageTitle('Settings - Notifications')

  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const [hasHandledPurchaseReturn, setHasHandledPurchaseReturn] = useState(false)
  const [transactionsPage, setTransactionsPage] = useState(1)

  const { data: creditsResponse, isLoading: isCreditsLoading } = useQuery({
    ...smsCreditsIndexOptions(),
  })

  const { data: transactionsResponse, isLoading: isTransactionsLoading } = useQuery({
    ...smsCreditsTransactionsOptions({
      query: {
        page: transactionsPage,
        per_page: TRANSACTIONS_PER_PAGE,
      },
    }),
  })

  const credits = creditsResponse?.data

  useEffect(() => {
    const purchaseResult = searchParams.get('credit_purchase')

    if (!purchaseResult || hasHandledPurchaseReturn) {
      return
    }

    if (purchaseResult === 'success') {
      ToastHelper.success('SMS credits purchased successfully.')
    } else if (purchaseResult === 'cancelled') {
      ToastHelper.error('Credit purchase was cancelled.')
    }

    queryClient.invalidateQueries({
      queryKey: smsCreditsIndexQueryKey(),
    })

    queryClient.invalidateQueries({
      queryKey: smsCreditsTransactionsQueryKey(),
    })

    setHasHandledPurchaseReturn(true)

    searchParams.delete('credit_purchase')
    setSearchParams(searchParams, { replace: true })
  }, [searchParams, setSearchParams, queryClient, hasHandledPurchaseReturn])

  return (
    <TabLayout>
      <Breadcrumb
        items={[
          {
            label: 'Settings',
            icon: SettingsIcon,
            path: SettingsRoutePath.Settings,
          },
          {
            label: 'Notifications',
            path: SettingsRoutePath.SettingsNotification,
          },
        ]}
      />

      <div>
        <Text
          type="h2"
          weight="medium"
        >
          Notification Settings
        </Text>

        <Text
          type="s"
          className="text-gray-500 mt-1"
        >
          Manage SMS appointment reminders and credits.
        </Text>
      </div>

      <SmsRemindersSettings />

      <Divider />

      <div className="flex items-center justify-between">
        <div>
          <Text
            type="h2"
            weight="medium"
          >
            SMS Credits
          </Text>

          <Text
            type="s"
            className="text-gray-500 mt-1"
          >
            Monitor your credit balance and purchase top-ups when needed.
          </Text>
        </div>

        <PurchaseCredits bundles={credits?.bundles ?? []} />
      </div>

      <div className="space-y-6">
        <SmsCreditsBalance
          data={credits}
          isLoading={isCreditsLoading}
        />

        <SmsCreditsTransactionHistory
          transactions={transactionsResponse?.data ?? []}
          isLoading={isTransactionsLoading}
          currentPage={transactionsPage}
          totalPages={transactionsResponse?.meta.last_page ?? 1}
          totalItems={transactionsResponse?.meta.total ?? 0}
          onPageChange={setTransactionsPage}
        />
      </div>
    </TabLayout>
  )
}
